import React from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  const { user, userProfile, isAuthenticated } = useAuth();

  const missionValues = [
    {
      icon: 'BookOpen',
      title: 'Accessibility First',
      description: 'Breaking down barriers to Shakespeare education through modern, engaging technology that meets students where they are.'
    },
    {
      icon: 'Users',
      title: 'Student-Centered',
      description: 'Designed with students in mind, our platform transforms complex Shakespearean texts into achievable learning experiences.'
    },
    {
      icon: 'Zap',
      title: 'Innovation in Learning',
      description: 'Combining gamification, interactive tools, and collaborative features to make classic literature relevant and exciting.'
    },
    {
      icon: 'Award',
      title: 'Excellence in Education',
      description: 'Empowering educators with comprehensive tools while helping students achieve academic success and genuine literary appreciation.'
    }
  ];

  const teamHighlights = [
    {
      icon: 'Lightbulb',
      title: 'Educational Expertise',
      description: 'Founded by experienced educators who understand the challenges students face with Shakespeare'
    },
    {
      icon: 'Code',
      title: 'Technology Innovation',
      description: 'Built by developers passionate about creating meaningful educational technology'
    },
    {
      icon: 'Heart',
      title: 'Student Success',
      description: 'Dedicated to transforming how students engage with and understand classic literature'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - BardPlay</title>
        <meta name="description" content="Learn about BardPlay's mission to make Shakespeare accessible and engaging for modern learners through innovative educational technology." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Hero Section */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-16">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Making Shakespeare <span className="text-primary">Accessible</span> for Everyone
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                BardPlay transforms how students interact with Shakespeare's works, combining modern technology 
                with timeless literature to create engaging, effective learning experiences.
              </p>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 md:p-12 border border-white/10">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Target" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Our Mission</h2>
                  <p className="text-lg text-white/80 leading-relaxed">
                    To revolutionize Shakespeare education by creating an interactive platform that makes 
                    classic literature engaging, understandable, and achievable for students of all learning 
                    styles. We believe every student deserves access to quality educational tools that transform 
                    academic challenges into opportunities for growth and discovery.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                The principles that guide everything we do at BardPlay
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {missionValues?.map((value, index) => (
                <div 
                  key={index}
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name={value?.icon} size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{value?.title}</h3>
                      <p className="text-white/70 leading-relaxed">{value?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What Makes Us Different */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What Makes Us Different</h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                We're more than just another educational platform
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {teamHighlights?.map((highlight, index) => (
                <div 
                  key={index}
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 text-center hover:scale-105 transition-transform duration-300"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon name={highlight?.icon} size={28} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{highlight?.title}</h3>
                  <p className="text-white/70">{highlight?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl p-8 md:p-12 border border-white/10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Join Our Educational Revolution
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                Whether you're a student looking to master Shakespeare or an educator seeking 
                innovative teaching tools, BardPlay is here to support your journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/registration"
                  className="px-8 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-300"
                >
                  Get Started Free
                </Link>
                <Link 
                  to="/contact-us"
                  className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors duration-300 border border-white/20"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AboutUs;